import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../common'

import { ApiSource } from './api-source'
import { DmsMapping } from './dms-mapping'

/**
 * Configures a {@link Connector.Definition} for a specific third-party system
 * instance. The definition describes reusable imports and mappings, while the
 * connector provides values that vary between connections, such as the base
 * URL.
 */
export interface Connector
  extends common.Identified<Connector.Id>,
    common.Named {
  /**
   * @see {@link Connector.Definition.Id}
   */
  definitionId: Connector.Definition.Id
  /**
   * Base URL of the customer's third-party system. API paths from the selected
   * definition are appended to this URL.
   *
   * @example 'https://customer.atlassian.net'
   */
  baseUrl: string
  /** Latest import run and its per-import progress. */
  lastRun?: Connector.Run
}
export namespace Connector {
  /** Connector data supplied when a connector is created. */
  export type Input = Omit<Connector, 'id' | 'lastRun'>

  /** Connector fields changed during an update. */
  export type Updates = Partial<Omit<Connector, 'id'>>

  export const isConnector: TypeChecker<Connector> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.intersection(
        common.Identified.getTypeChecker(Id.isId),
        common.Named.isNamed,
        TypeChecker.Factory.object<Omit<Connector, 'id' | 'name'>>({
          baseUrl: TypeChecker.isString,
          definitionId: Definition.Id.isId,
          lastRun: TypeChecker.Factory.optional(Run.isRun),
        }),
      ),
  ).usingAlias('integrations.Connector')

  export const assertIsConnector: TypeAsserter<Connector> =
    TypeAsserter.Factory.from(isConnector)

  /** Stable identifier for a persisted connector. */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.isString.usingAlias(
      'integrations.Connector.Id',
    )

    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /** Persisted progress for the latest execution of this connector. */
  export interface Run extends common.Identified<Run.Id> {
    /** ISO timestamp at which this run started. */
    startedAt: string
    /** ISO timestamp at which this run reached a terminal status. */
    completedAt?: string
    /** User-friendly failure that prevented the whole run from starting. */
    error?: string
    /** One result for every definition import, in definition order. */
    imports: readonly Run.Import[]
  }
  export namespace Run {
    /** Unique identifier that prevents updates from an older run winning. */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = string
    export namespace Id {
      export const isId: TypeChecker<Id> = TypeChecker.isString.usingAlias(
        'integrations.Connector.Run.Id',
      )
    }

    export enum Status {
      Running = 'running',
      Succeeded = 'succeeded',
      Partial = 'partial',
      Failed = 'failed',
    }
    export namespace Status {
      const VALUES = new Set<Status>([
        Status.Failed,
        Status.Partial,
        Status.Running,
        Status.Succeeded,
      ])
      export const isStatus: TypeChecker<Status> =
        TypeChecker.Factory.oneOf(VALUES)

      /** Derives the connector-run status from its individual imports. */
      export function fromImports(
        imports: readonly Run.Import[],
      ): Status {
        const statuses = new Set(imports.map(({ status }) => status))
        if (statuses.has(Import.Status.Running)) {
          return Status.Running
        }
        if (statuses.size === 1 && statuses.has(Import.Status.Succeeded)) {
          return Status.Succeeded
        }
        if (statuses.has(Import.Status.Succeeded)) {
          return Status.Partial
        }
        return Status.Failed
      }
    }

    /** Progress and result for one API path in a connector definition. */
    export interface Import {
      /** API path used as this import's display name. */
      path: string
      status: Import.Status
      /** User-friendly reason the import failed. */
      error?: string
    }
    export namespace Import {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export enum Status {
        Running = 'running',
        Succeeded = 'succeeded',
        Failed = 'failed',
      }
      export namespace Status {
        const VALUES = new Set<Status>([
          Status.Failed,
          Status.Running,
          Status.Succeeded,
        ])
        export const isStatus: TypeChecker<Status> =
          TypeChecker.Factory.oneOf(VALUES)
      }

      export const isImport: TypeChecker<Import> = TypeChecker.Factory.object({
        error: TypeChecker.Factory.optional(TypeChecker.isString),
        path: TypeChecker.isString,
        status: Status.isStatus,
      })
    }

    export const isRun: TypeChecker<Run> = TypeChecker.Factory.object({
      completedAt: TypeChecker.Factory.optional(TypeChecker.isString),
      error: TypeChecker.Factory.optional(TypeChecker.isString),
      id: Id.isId,
      imports: TypeChecker.Factory.list(Import.isImport),
      startedAt: TypeChecker.isString,
    })
  }

  /** Name of the Secrets Manager secret holding a connector's API key. */
  export type SecretName = string
  export namespace SecretName {
    /**
     * Shared by every connector secret so that the IAM policy granting the
     * runtime access to them can be scoped to one ARN pattern instead of
     * every secret in the account.
     */
    export const PREFIX = 'attain-apps-integrations-connector-'

    /** Builds the secret name holding the given connector's API key. */
    export function from(connectorId: Id): SecretName {
      return `${PREFIX}${connectorId}`
    }
  }

  /** Reusable imports and mappings supported by a third-party system. */
  export interface Definition
    extends common.Identified<Definition.Id>,
      common.Named {
    /** @see {@link Connector.Definition.Import} */
    imports: readonly Definition.Import[]
  }
  export namespace Definition {
    /** Definition data supplied when a connector definition is created. */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Input = Omit<Definition, 'id'>

    /** Definition fields changed during an update. */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Updates = Partial<Input>

    /** Stable identifier for a connector definition. */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = string
    export namespace Id {
      export const isId: TypeChecker<Id> = TypeChecker.isString.usingAlias(
        'integrations.Connector.Definition.Id',
      )

      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)
    }

    /** Defines how one API endpoint is imported into DMS. */
    export interface Import {
      /** @see {@link ApiSource} */
      api: ApiSource
      /** @see {@link DmsMapping} */
      mapping: DmsMapping
    }
    export namespace Import {
      export const isImport: TypeChecker<Import> =
        TypeChecker.Factory.object<Import>({
          api: ApiSource.isApiSource,
          mapping: DmsMapping.isDmsMapping,
        }).usingAlias('integrations.Connector.Definition.Import')

      export const assertIsImport: TypeAsserter<Import> =
        TypeAsserter.Factory.from(isImport)
    }

    export const isDefinition: TypeChecker<Definition> =
      TypeChecker.Factory.intersection(
        common.Identified.getTypeChecker(Id.isId),
        common.Named.isNamed,
        TypeChecker.Factory.object<Omit<Definition, 'id' | 'name'>>({
          imports: TypeChecker.Factory.list(Import.isImport),
        }),
      ).usingAlias('integrations.Connector.Definition')

    export const assertIsDefinition: TypeAsserter<Definition> =
      TypeAsserter.Factory.from(isDefinition)

    /** Returns the distinct target model IDs in import declaration order. */
    export function getModelIds(
      definition: Pick<Definition, 'imports'>,
    ): readonly DmsMapping['modelId'][] {
      return Array.from(
        new Set(definition.imports.map(({ mapping }) => mapping.modelId)),
      )
    }
  }
}
